import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Settings, Loader2, Check } from "lucide-react";

export default function VaultSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ auto_logout_minutes: 30, token_display_format: "masked", require_pin: false, notifications_enabled: true, two_factor_enabled: false, allowed_ip_ranges: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    base44.entities.VaultSettings.list().then((list) => {
      if (list?.length) { setSettings(list[0]); setForm({ ...list[0] }); }
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    if (settings?.id) {
      await base44.entities.VaultSettings.update(settings.id, form);
    } else {
      const created = await base44.entities.VaultSettings.create(form);
      setSettings(created);
    }
    await base44.entities.ActivityLog.create({ action: "settings_changed", entity_type: "VaultSettings", details: "Vault settings updated" });
    setSaved(true);
    setSaving(false);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Vault Settings</h1>
      <p className="text-sm text-slate-500 mb-6">Configure security and display preferences.</p>

      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Security</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Auto-logout after (minutes)</label>
              <select value={form.auto_logout_minutes} onChange={(e) => setForm((f) => ({ ...f, auto_logout_minutes: Number(e.target.value) }))}
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900">
                {[5, 10, 15, 30, 60, 120].map((v) => <option key={v} value={v}>{v} minutes</option>)}
              </select>
            </div>

            {[
              { key: "require_pin", label: "Require PIN to view tokens", desc: "Adds an extra layer of authentication before revealing token data." },
              { key: "two_factor_enabled", label: "Two-factor authentication", desc: "Require a second factor when logging in." },
              { key: "notifications_enabled", label: "Security notifications", desc: "Receive alerts on suspicious activity or new logins." },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-900">{label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, [key]: !f[key] }))}
                  className={`relative flex-shrink-0 h-6 w-11 rounded-full transition-colors ${form[key] ? "bg-slate-900" : "bg-slate-200"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${form[key] ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            ))}

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Allowed IP ranges (comma-separated, optional)</label>
              <input value={form.allowed_ip_ranges || ""} onChange={(e) => setForm((f) => ({ ...f, allowed_ip_ranges: e.target.value }))}
                placeholder="192.168.1.0/24, 10.0.0.0/8"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Display</h2>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Default token display format</label>
            <div className="grid grid-cols-3 gap-2">
              {["masked", "partial", "hidden"].map((opt) => (
                <button key={opt} type="button" onClick={() => setForm((f) => ({ ...f, token_display_format: opt }))}
                  className={`rounded-xl border py-2.5 text-sm font-medium transition ${form.token_display_format === opt ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button onClick={save} disabled={saving}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition disabled:opacity-60">
          {saved ? <><Check className="h-4 w-4" /> Saved</> : saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save settings"}
        </button>
      </div>
    </div>
  );
}